import { FileOutlined } from '@ant-design/icons'
import { Tag, Typography } from 'antd'
import { relativeTimeString } from 'enrollment-platform/utils/relative-time-string'
import React from 'react'
import {
  CensusUploadJobChannel,
  CensusUploadJobDto,
} from '../api/dto/census-upload-job.dto'
import CensusQueueActionTag from '../components/CensusQueueActionTag'
import CensusQueueActionsCell from '../components/CensusQueueActionsCell'
import CensusQueueStatusCell from '../components/CensusQueueStatusCell'

const { Text } = Typography

const CHANNEL_LABELS: Record<CensusUploadJobChannel, string> = {
  API: 'API Integration',
  AMA: 'AMA',
  MANUAL_UPLOAD: 'Manual upload',
}

// Gets AGGrid column definitions for the Queue section of Census Management
export const getCensusQueueColDefs = ({
  clientNameLookup,
  onChanged,
  showAction = false,
  showChannel = false,
}: {
  clientNameLookup: Record<string, string>
  onChanged: () => void
  // The HRIS Queue mixes AMA uploads and submits; CMv2 only lists uploads
  showAction?: boolean
  // Census Sync Visibility mixes channels; the other pages list one
  showChannel?: boolean
}) => [
  {
    headerName: 'Uploaded',
    field: 'createdAt',
    flex: 1,
    sortable: false,
    cellRenderer: (params) => (
      <Text className="textSm">{relativeTimeString(params.value, 1)}</Text>
    ),
  },
  ...(showAction
    ? [
        {
          headerName: 'Action',
          field: 'action',
          width: 130,
          sortable: false,
          cellRenderer: (params) =>
            params.value ? (
              <CensusQueueActionTag action={params.value} />
            ) : null,
        },
      ]
    : []),
  ...(showChannel
    ? [
        {
          headerName: 'Channel',
          field: 'channel',
          width: 150,
          sortable: false,
          cellRenderer: (params) => (
            <Tag
              color={params.value === 'AMA' ? 'blue' : 'default'}
              style={{ marginInlineEnd: 0 }}
            >
              {CHANNEL_LABELS[params.value] ?? params.value}
            </Tag>
          ),
        },
      ]
    : []),
  {
    headerName: 'Customer',
    field: 'clientPublicKey',
    flex: 1,
    sortable: false,
    cellRenderer: (params) => (
      <Text className="textEmphasis">
        {clientNameLookup[params.value] ?? 'loading...'}
      </Text>
    ),
  },
  {
    headerName: 'Uploaded by',
    field: 'uploadedBy',
    flex: 1,
    sortable: false,
    cellRenderer: (params) => (
      <Text className="textSm">{params?.value ?? '-'}</Text>
    ),
  },
  {
    headerName: 'File',
    field: 'fileName',
    flex: 1,
    sortable: false,
    cellRenderer: (params) => (
      <>
        <FileOutlined style={{ fontSize: '14px', marginRight: '8px' }} />
        <Text className="textSm textGray">{params.value}</Text>
      </>
    ),
  },
  {
    headerName: 'Status',
    colId: 'status',
    // The grid redraws a cell only when its value changes; status alone stays PROCESSING
    valueGetter: (params) =>
      params.data
        ? `${params.data.status}:${params.data.percent}:${params.data.stage}`
        : null,
    flex: 1.4,
    sortable: false,
    cellRenderer: (params) =>
      params.data ? <CensusQueueStatusCell job={params.data} /> : null,
  },
  {
    headerName: '',
    field: 'actions',
    width: 190,
    sortable: false,
    filter: false,
    suppressHeaderMenuButton: true,
    cellRenderer: (params) =>
      params.data ? (
        <CensusQueueActionsCell
          job={params.data as CensusUploadJobDto}
          onChanged={onChanged}
        />
      ) : null,
  },
]
